import type {
  IPaymentResult,
  IWalletSplit,
  PaymentGateway,
} from "@/src/shared/features/payment/types";

export const paymentService = {
  /** Whole taka, as Laravel floors the balance before spending it. */
  spendable(balance: number | null | undefined): number {
    return Math.max(0, Math.floor(Number(balance) || 0));
  },

  keptInWallet(
    balance: number | null | undefined,
    walletAmount: number,
  ): number {
    return Math.max(0, paymentService.spendable(balance) - walletAmount);
  },

  isWalletOnly(
    result: Pick<IPaymentResult, "amount" | "wallet_amount"> | undefined,
  ): boolean {
    return !!result && (result.wallet_amount ?? 0) > 0 && result.amount === 0;
  },

  /** What leaves the guest now: the wallet can pay past the chosen amount, up to the full due. */
  payNow(
    split: Pick<IWalletSplit, "isOn" | "walletAmount" | "gatewayAmount">,
    amount: number,
  ): number {
    return split.isOn ? split.walletAmount + split.gatewayAmount : amount;
  },

  /**
   * Laravel's `provider` and `action`, as the app's `_gatewayAction`: a saved number pays on its
   * agreement, a new bKash number is saved (agreement) unless unticked, the rest is one-time.
   */
  gatewayRequest(
    gateway: PaymentGateway,
    saveBkash: boolean,
    isPaidByWalletOnly: boolean,
  ): {
    provider: "bkash" | "ssl";
    action: "payment" | "agreement" | "one-time";
  } {
    if (isPaidByWalletOnly) return { provider: "bkash", action: "one-time" };
    if (gateway === "ssl") return { provider: "ssl", action: "one-time" };
    if (gateway === "savedBkash")
      return { provider: "bkash", action: "payment" };
    return { provider: "bkash", action: saveBkash ? "agreement" : "one-time" };
  },

  nextUrl(
    bookingId: string | number,
    result: Pick<IPaymentResult, "paid" | "payment_url">,
  ): string | null {
    if (result.paid) return `/payment/success?order=${bookingId}`;
    return typeof result.payment_url === "string" && result.payment_url
      ? result.payment_url
      : null;
  },
};
